import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    LayoutDashboard, Package, ShoppingBag, Tag, Utensils, Mail,
    Plus, Pencil, Trash2, ArrowLeft, LogOut, X, DollarSign, ImagePlus,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const ORDER_STATUSES = ["initiated", "confirmed", "preparing", "ready", "out-for-delivery", "shipped", "delivered", "cancelled"];

function money(n) {
    return `$${Number(n || 0).toFixed(2)}`;
}

/* ---------------------------- Overview ---------------------------- */
function Overview() {
    const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
    const cards = [
        { label: "Revenue (paid)", value: money(stats?.revenue), icon: DollarSign },
        { label: "Paid Orders", value: stats?.orders_paid ?? "—", icon: ShoppingBag },
        { label: "Total Orders", value: stats?.orders_total ?? "—", icon: ShoppingBag },
        { label: "Products", value: stats?.products ?? "—", icon: Package },
        { label: "New Catering", value: stats?.catering_new ?? "—", icon: Utensils },
        { label: "New Messages", value: stats?.contact_new ?? "—", icon: Mail },
    ];
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-overview">
            {cards.map((c) => (
                <div key={c.label} className="rounded-2xl border border-caramel/10 bg-white p-5 shadow-warm">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-mocha">{c.label}</p>
                        <c.icon size={18} className="text-caramel" />
                    </div>
                    <p className="mt-2 font-serif text-3xl text-roast">{c.value}</p>
                </div>
            ))}
        </div>
    );
}

/* ---------------------------- Products ---------------------------- */
const EMPTY_PRODUCT = {
    name: "", flavor: "", short_desc: "", description: "", price: 12,
    badge: "", category: "individual", ingredients: "", allergens: "",
    serving: "Each jar serves 1–2. Trays serve 8 or 16.", storage: "Keep refrigerated. Best enjoyed within 3 days.",
    images: [], sold_out: false, in_stock: true, rating: 4.9, review_count: 0,
};

function ProductDialog({ open, onClose, product, onSaved }) {
    const [form, setForm] = useState(product || EMPTY_PRODUCT);
    const [saving, setSaving] = useState(false);
    const [imgUrl, setImgUrl] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    React.useEffect(() => { setForm(product || EMPTY_PRODUCT); }, [product]);

    const addImageFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 1.5 * 1024 * 1024) { toast.error("Image too large (max 1.5MB). Try a smaller file."); return; }
        const reader = new FileReader();
        reader.onload = () => setForm((f) => ({ ...f, images: [...(f.images || []), reader.result] }));
        reader.readAsDataURL(file);
        e.target.value = "";
    };
    const addImageUrl = () => {
        if (!imgUrl.trim()) return;
        setForm((f) => ({ ...f, images: [...(f.images || []), imgUrl.trim()] }));
        setImgUrl("");
    };
    const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

    const save = async () => {
        if (!form.name?.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            const payload = { ...form, price: Number(form.price), review_count: Number(form.review_count) || 0, rating: Number(form.rating) || 4.9 };
            if (product?.id) await api.put(`/admin/products/${product.id}`, payload);
            else await api.post("/admin/products", payload);
            toast.success(product?.id ? "Product updated" : "Product created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-cream-50" data-testid="product-dialog">
                <DialogHeader><DialogTitle className="font-serif text-2xl text-roast">{product?.id ? "Edit Product" : "New Product"}</DialogTitle><DialogDescription className="text-mocha">Fill in the pudding details, price and photos, then save.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Name"><Input data-testid="pf-name" value={form.name} onChange={set("name")} /></Field>
                        <Field label="Flavor"><Input value={form.flavor} onChange={set("flavor")} /></Field>
                        <Field label="Price ($)"><Input data-testid="pf-price" type="number" step="0.5" value={form.price} onChange={set("price")} /></Field>
                        <BadgeField value={form.badge} onChange={set("badge")} />
                    </div>
                    <Field label="Short description"><Input value={form.short_desc} onChange={set("short_desc")} /></Field>
                    <Field label="Full description"><Textarea rows={3} value={form.description} onChange={set("description")} /></Field>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Ingredients"><Textarea rows={2} value={form.ingredients} onChange={set("ingredients")} /></Field>
                        <Field label="Allergens"><Textarea rows={2} value={form.allergens} onChange={set("allergens")} /></Field>
                    </div>

                    <Field label="Photos">
                        <div className="flex flex-wrap gap-2">
                            {(form.images || []).map((img, i) => (
                                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-caramel/20">
                                    <img src={img} alt="" className="h-full w-full object-cover" />
                                    <button type="button" onClick={() => removeImage(i)} className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-roast/80 text-cream-50" data-testid={`pf-img-remove-${i}`}><X size={12} /></button>
                                </div>
                            ))}
                            <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border border-dashed border-caramel/40 text-caramel hover:bg-cream-200" data-testid="pf-img-upload">
                                <ImagePlus size={22} />
                                <input type="file" accept="image/*" className="hidden" onChange={addImageFile} />
                            </label>
                        </div>
                        <div className="mt-2 flex gap-2">
                            <Input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="…or paste an image URL" />
                            <Button type="button" variant="outline" onClick={addImageUrl}>Add URL</Button>
                        </div>
                    </Field>

                    <label className="flex items-center justify-between rounded-xl border border-caramel/15 bg-white px-4 py-2.5">
                        <span className="text-sm font-medium text-roast">Sold out</span>
                        <Switch checked={!!form.sold_out} onCheckedChange={(v) => setForm((f) => ({ ...f, sold_out: v }))} data-testid="pf-soldout" />
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-caramel text-cream-50 hover:bg-caramel-dark" data-testid="pf-save">{saving ? "Saving…" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Field = ({ label, children }) => (
    <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mocha">{label}</label>{children}</div>
);

function BadgeField({ value, onChange }) {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["admin-badges"], queryFn: async () => (await api.get("/admin/badges")).data });
    const options = data?.options || [];
    const [manage, setManage] = useState(false);
    const [list, setList] = useState([]);
    const [nb, setNb] = useState("");
    const [saving, setSaving] = useState(false);

    const openManage = () => { setList(options); setManage(true); };
    const add = () => { const v = nb.trim(); if (!v || list.includes(v)) { setNb(""); return; } setList([...list, v]); setNb(""); };
    const removeAt = (i) => setList(list.filter((_, idx) => idx !== i));
    const save = async () => {
        setSaving(true);
        try {
            const res = await api.put("/admin/badges", { options: list });
            qc.setQueryData(["admin-badges"], res.data);
            qc.invalidateQueries({ queryKey: ["admin-badges"] });
            if (value && !res.data.options.includes(value)) onChange({ target: { value: "" } });
            toast.success("Badge options saved");
            setManage(false);
        } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
    };

    const selectOpts = value && !options.includes(value) ? [value, ...options] : options;

    return (
        <Field label="Badge (optional)">
            <div className="flex gap-2">
                <select value={value || ""} onChange={onChange} data-testid="pf-badge" className="h-10 w-full rounded-md border border-caramel/20 bg-white px-3 text-sm">
                    <option value="">None</option>
                    {selectOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <Button type="button" variant="outline" onClick={openManage} data-testid="pf-badge-manage" className="shrink-0">Edit list</Button>
            </div>
            {manage && (
                <div className="mt-2 rounded-xl border border-caramel/20 bg-cream-100 p-3" data-testid="badge-manage-panel">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-mocha">Badge options</p>
                    <div className="space-y-1.5">
                        {list.map((o, i) => (
                            <div key={o} className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5 text-sm text-roast" data-testid={`badge-opt-${i}`}>
                                <span>{o}</span>
                                <button type="button" onClick={() => removeAt(i)} className="text-mocha hover:text-destructive" data-testid={`badge-remove-${i}`}><X size={14} /></button>
                            </div>
                        ))}
                        {list.length === 0 && <p className="text-xs text-mocha">No options — add one below.</p>}
                    </div>
                    <div className="mt-2 flex gap-2">
                        <Input value={nb} onChange={(e) => setNb(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add a badge…" data-testid="badge-new-input" />
                        <Button type="button" variant="outline" onClick={add} data-testid="badge-add-btn">Add</Button>
                    </div>
                    <div className="mt-2 flex justify-end gap-2">
                        <Button type="button" variant="ghost" onClick={() => setManage(false)}>Cancel</Button>
                        <Button type="button" onClick={save} disabled={saving} className="bg-caramel text-cream-50 hover:bg-caramel-dark" data-testid="badge-save-btn">{saving ? "Saving…" : "Save list"}</Button>
                    </div>
                </div>
            )}
        </Field>
    );
}

function Products() {
    const qc = useQueryClient();
    const { data: products = [] } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/admin/products")).data });
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-products"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

    const del = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
        try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); refresh(); }
        catch (err) { toast.error(apiError(err)); }
    };

    return (
        <div data-testid="admin-products">
            <div className="mb-4 flex justify-end">
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-caramel text-cream-50 hover:bg-caramel-dark" data-testid="add-product-btn"><Plus size={16} className="mr-1" /> New Product</Button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-caramel/10 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-cream-100 text-left text-mocha">
                        <tr><th className="p-3">Photo</th><th className="p-3">Name</th><th className="p-3">Price</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-t border-caramel/10" data-testid={`product-row-${p.slug}`}>
                                <td className="p-3"><div className="h-12 w-12 overflow-hidden rounded-lg bg-cream-200">{p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover" />}</div></td>
                                <td className="p-3 font-medium text-roast">{p.name}</td>
                                <td className="p-3 text-roast">{money(p.price)}</td>
                                <td className="p-3">{p.sold_out ? <Badge variant="destructive">Sold out</Badge> : <Badge className="bg-banana/40 text-roast">Available</Badge>}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => { setEditing(p); setOpen(true); }} className="mr-1 rounded-lg p-2 text-caramel hover:bg-cream-200" data-testid={`edit-product-${p.slug}`}><Pencil size={16} /></button>
                                    <button onClick={() => del(p)} className="rounded-lg p-2 text-mocha hover:bg-cream-200 hover:text-destructive" data-testid={`delete-product-${p.slug}`}><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {open && <ProductDialog open={open} product={editing} onClose={() => setOpen(false)} onSaved={refresh} />}
        </div>
    );
}

/* ---------------------------- Orders ---------------------------- */
function Orders() {
    const qc = useQueryClient();
    const { data: orders = [] } = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data });

    const updateStatus = async (o, status) => {
        try { await api.patch(`/admin/orders/${o.id}`, { status }); toast.success("Order updated"); qc.invalidateQueries({ queryKey: ["admin-orders"] }); }
        catch (err) { toast.error(apiError(err)); }
    };

    return (
        <div className="overflow-x-auto rounded-2xl border border-caramel/10 bg-white" data-testid="admin-orders">
            <table className="w-full text-sm">
                <thead className="bg-cream-100 text-left text-mocha">
                    <tr><th className="p-3">Order #</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Fulfillment</th><th className="p-3">Status</th></tr>
                </thead>
                <tbody>
                    {orders.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-mocha">No orders yet.</td></tr>}
                    {orders.map((o) => (
                        <tr key={o.id} className="border-t border-caramel/10" data-testid={`order-row-${o.order_number}`}>
                            <td className="p-3 font-medium text-roast">{o.order_number}</td>
                            <td className="p-3 text-roast">{o.name}<div className="text-xs text-mocha">{o.email}</div></td>
                            <td className="p-3 text-roast">{money(o.total)}</td>
                            <td className="p-3">{o.payment_status === "paid" ? <Badge className="bg-green-100 text-green-700">paid</Badge> : <Badge variant="outline">{o.payment_status}</Badge>}</td>
                            <td className="p-3 text-mocha">{o.fulfillment}</td>
                            <td className="p-3">
                                <select value={o.status} onChange={(e) => updateStatus(o, e.target.value)} className="rounded-lg border border-caramel/20 bg-white px-2 py-1.5 text-sm" data-testid={`order-status-${o.order_number}`}>
                                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------------------------- Discounts ---------------------------- */
function Discounts() {
    const qc = useQueryClient();
    const { data: codes = [] } = useQuery({ queryKey: ["admin-discounts"], queryFn: async () => (await api.get("/admin/discounts")).data });
    const [form, setForm] = useState({ code: "", type: "percent", value: 10, active: true });
    const refresh = () => qc.invalidateQueries({ queryKey: ["admin-discounts"] });

    const create = async () => {
        if (!form.code.trim()) { toast.error("Enter a code"); return; }
        try { await api.post("/admin/discounts", { ...form, value: Number(form.value) }); toast.success("Discount created"); setForm({ code: "", type: "percent", value: 10, active: true }); refresh(); }
        catch (err) { toast.error(apiError(err)); }
    };
    const toggle = async (c) => {
        try { await api.patch(`/admin/discounts/${c.code}`, { code: c.code, type: c.type, value: c.value, active: !c.active }); refresh(); }
        catch (err) { toast.error(apiError(err)); }
    };
    const del = async (c) => {
        if (!window.confirm(`Delete code ${c.code}?`)) return;
        try { await api.delete(`/admin/discounts/${c.code}`); toast.success("Deleted"); refresh(); }
        catch (err) { toast.error(apiError(err)); }
    };

    return (
        <div data-testid="admin-discounts">
            <div className="mb-4 grid gap-2 rounded-2xl border border-caramel/10 bg-white p-4 sm:grid-cols-[1fr_140px_120px_auto] sm:items-end">
                <Field label="Code"><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="SUMMER20" data-testid="disc-code" /></Field>
                <Field label="Type">
                    <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="h-10 w-full rounded-md border border-caramel/20 bg-white px-3 text-sm" data-testid="disc-type">
                        <option value="percent">Percent %</option>
                        <option value="fixed">Fixed $</option>
                    </select>
                </Field>
                <Field label="Value"><Input type="number" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} data-testid="disc-value" /></Field>
                <Button onClick={create} className="bg-caramel text-cream-50 hover:bg-caramel-dark" data-testid="disc-create"><Plus size={16} className="mr-1" /> Add</Button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-caramel/10 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-cream-100 text-left text-mocha"><tr><th className="p-3">Code</th><th className="p-3">Type</th><th className="p-3">Value</th><th className="p-3">Active</th><th className="p-3 text-right">Actions</th></tr></thead>
                    <tbody>
                        {codes.map((c) => (
                            <tr key={c.code} className="border-t border-caramel/10" data-testid={`discount-row-${c.code}`}>
                                <td className="p-3 font-medium text-roast">{c.code}</td>
                                <td className="p-3 text-mocha">{c.type}</td>
                                <td className="p-3 text-roast">{c.type === "percent" ? `${c.value}%` : money(c.value)}</td>
                                <td className="p-3"><Switch checked={!!c.active} onCheckedChange={() => toggle(c)} data-testid={`disc-toggle-${c.code}`} /></td>
                                <td className="p-3 text-right"><button onClick={() => del(c)} className="rounded-lg p-2 text-mocha hover:bg-cream-200 hover:text-destructive" data-testid={`disc-delete-${c.code}`}><Trash2 size={16} /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ---------------------------- Inquiries (catering + contact) ---------------------------- */
function Inquiries({ kind }) {
    const qc = useQueryClient();
    const key = kind === "catering" ? "admin-catering" : "admin-contact";
    const { data: items = [] } = useQuery({ queryKey: [key], queryFn: async () => (await api.get(`/admin/${kind}`)).data });
    const mark = async (item, status) => {
        try { await api.patch(`/admin/${kind}/${item.id}`, { status }); qc.invalidateQueries({ queryKey: [key] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); }
        catch (err) { toast.error(apiError(err)); }
    };
    return (
        <div className="space-y-3" data-testid={`admin-${kind}`}>
            {items.length === 0 && <p className="rounded-2xl border border-caramel/10 bg-white p-6 text-center text-mocha">No {kind} inquiries yet.</p>}
            {items.map((it) => (
                <div key={it.id} className="rounded-2xl border border-caramel/10 bg-white p-4" data-testid={`${kind}-item-${it.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <p className="font-medium text-roast">{it.name} <span className="text-sm font-normal text-mocha">· {it.email}</span></p>
                            <p className="text-xs text-mocha">{new Date(it.created_at).toLocaleString()}{kind === "catering" && it.event_type ? ` · ${it.event_type}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant={it.status === "new" ? "default" : "outline"} className={it.status === "new" ? "bg-banana/50 text-roast" : ""}>{it.status}</Badge>
                            <select value={it.status} onChange={(e) => mark(it, e.target.value)} className="rounded-lg border border-caramel/20 bg-white px-2 py-1.5 text-sm" data-testid={`${kind}-status-${it.id}`}>
                                <option value="new">new</option>
                                <option value="in-progress">in-progress</option>
                                <option value="done">done</option>
                            </select>
                        </div>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-roast/80">{it.message || "—"}</p>
                    {kind === "catering" && (
                        <p className="mt-1 text-xs text-mocha">{it.event_date ? `Date: ${it.event_date}` : ""} {it.guest_count ? `· Guests: ${it.guest_count}` : ""} {it.phone ? `· ${it.phone}` : ""}</p>
                    )}
                </div>
            ))}
        </div>
    );
}

/* ---------------------------- Shell ---------------------------- */
const TABS = [
    { v: "overview", label: "Overview", icon: LayoutDashboard },
    { v: "products", label: "Products", icon: Package },
    { v: "orders", label: "Orders", icon: ShoppingBag },
    { v: "discounts", label: "Discounts", icon: Tag },
    { v: "catering", label: "Catering", icon: Utensils },
    { v: "contact", label: "Messages", icon: Mail },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    if (user === undefined) return <div className="grid min-h-screen place-items-center text-mocha">Loading…</div>;
    if (!user) { navigate("/login"); return null; }
    if (user.role !== "admin") {
        return (
            <div className="grid min-h-screen place-items-center px-6 text-center" data-testid="admin-denied">
                <div>
                    <p className="font-serif text-3xl text-roast">Admins only</p>
                    <p className="mt-2 text-mocha">This area is restricted to store administrators.</p>
                    <Link to="/" className="mt-4 inline-block text-caramel underline">Back to site</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream-50" data-testid="admin-dashboard">
            <header className="sticky top-0 z-30 flex items-center justify-between border-b border-caramel/10 bg-cream-50/90 px-5 py-3 backdrop-blur-xl lg:px-8">
                <div className="flex items-center gap-3">
                    <span className="font-script text-3xl text-roast">Sweet Pearls</span>
                    <Badge className="bg-banana/50 text-roast">Admin</Badge>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-roast hover:bg-cream-200" data-testid="admin-back-site"><ArrowLeft size={16} /> View site</Link>
                    <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-roast hover:bg-cream-200" data-testid="admin-logout"><LogOut size={16} /> Log out</button>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
                <h1 className="mb-6 font-serif text-3xl font-semibold text-roast">Dashboard</h1>
                <Tabs defaultValue="overview">
                    <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-1 bg-cream-100 p-1">
                        {TABS.map((t) => (
                            <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`} className="gap-1.5 data-[state=active]:bg-white data-[state=active]:text-caramel">
                                <t.icon size={15} /> {t.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <TabsContent value="overview"><Overview /></TabsContent>
                    <TabsContent value="products"><Products /></TabsContent>
                    <TabsContent value="orders"><Orders /></TabsContent>
                    <TabsContent value="discounts"><Discounts /></TabsContent>
                    <TabsContent value="catering"><Inquiries kind="catering" /></TabsContent>
                    <TabsContent value="contact"><Inquiries kind="contact" /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
